import React, { useMemo, useState, useEffect, useRef } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import type { Lang } from '../utils/i18n';
import { useAccount, type AccountRechargeRecord } from '../contexts/AccountContext';

/** 估算用：灵活档灯光约 15 分/次；高档位以账户接口返回为准。放大 10 分/次 */
const CREDITS_PER_LIGHT_RENDER = 15;
const CREDITS_PER_AI_UPSCALE = 10;

// 与官网语种联动：仅中英文（与 index.html localStorage「lang」）
type UALang = 'en' | 'zh';

const UA_STRINGS: Record<UALang, Record<string, string>> = {
  en: {
    userAccount: 'User Account',
    remainingPoints: 'REMAINING POINTS',
    pointsUnit: 'pts',
    totalRecharged: 'Total Recharged',
    totalPoints: 'Total Points',
    rechargeHistory: 'Recharge History',
    rechargeTime: 'Time',
    rechargeAmount: 'Amount',
    rechargeCredits: 'Credits',
    expireDate: 'Expire',
    noRechargeHistory: 'No recharge history',
    singlePassLightMaps: 'Single pass: {n} lighting render(s)',
    singlePassAiUpscale: 'Single pass: {n} AI upscale(s)',
    accountBalance: 'ACCOUNT BALANCE',
    supportsSingle: 'Supports single-item rendering and AI upscaling',
    accountNormal: 'Account status normal',
    accountSyncing: 'Syncing account…',
    accountStale: 'Balance may be outdated — sync failed, showing last known balance',
    accountError: 'Could not sync account — showing last known balance if available',
    rechargeNow: 'Recharge Now',
    historyAlipay: 'Alipay recharge',
    historyAdmin: 'Admin credit adjustment',
    historyAdminGift: 'Admin credit gift',
    verifyEmail: 'Please verify your email to access your account. Check your email for the confirmation link.',
    pleaseLogin: 'Please log in to access your account.',
    accountNotFound: 'Account not found. Using default values. If this persists, please contact support.',
    accountExistsNoAccess: 'Account exists but cannot be accessed. Using default values.',
    permissionDenied: 'Permission denied. Please check database permissions or contact support.',
    initFailed: 'Account initialization failed. Using default values. You can still use the service normally.',
    loadFailed: 'Failed to load account information. Tap refresh to retry.',
    loadFailedRetry: 'Could not load account. Please tap refresh above.',
    permissionDeniedDb: 'Permission denied. Please check database permissions.',
    authError: 'Authentication error. Please try logging in again.',
    statConsumed: 'Consumed',
    statRechargeTimes: '{n} time(s)',
    statGiftIncluded: 'Incl. {n} gift pts',
    statUsageRecords: 'Usage records',
    compositionTitle: 'Points breakdown',
    compositionRemaining: 'Remaining',
    compositionConsumedLabel: 'Consumed',
    compositionGiftLabel: 'Gift points',
    ledgerSyncError: 'Account records failed to sync. Please refresh or contact support.',
  },
  zh: {
    userAccount: '用户账户',
    remainingPoints: '剩余积分',
    pointsUnit: '积分',
    totalRecharged: '累计充值',
    totalPoints: '累计获得',
    rechargeHistory: '充值历史',
    rechargeTime: '充值时间',
    rechargeAmount: '充值金额',
    rechargeCredits: '获得积分',
    expireDate: '过期时间',
    noRechargeHistory: '暂无充值记录',
    singlePassLightMaps: '单项：{n}张灯光图',
    singlePassAiUpscale: '单项：{n}张AI放大图',
    accountBalance: '当前账户余额',
    supportsSingle: '支持单项渲染与 AI 放大',
    accountNormal: '账户状态正常',
    accountSyncing: '账户同步中…',
    accountStale: '余额可能已过期：同步失败，显示上次成功同步的余额',
    accountError: '账户同步失败：如有历史余额则继续显示，不会显示为 0',
    rechargeNow: '立即充值',
    historyAlipay: '支付宝充值',
    historyAdmin: '管理员代充值',
    historyAdminGift: '管理员赠送',
    verifyEmail: '请先验证您的邮箱以访问账户信息。请检查您的邮箱并点击确认链接。',
    pleaseLogin: '请先登录以访问账户信息。',
    accountNotFound: '账户不存在，使用默认值。如果问题持续，请联系客服。',
    accountExistsNoAccess: '账户存在但无法访问，使用默认值。',
    permissionDenied: '权限被拒绝。请检查数据库权限或联系客服。',
    initFailed: '账户初始化失败，使用默认值。您仍可正常使用服务。',
    loadFailed: '获取账户信息失败，请点击右上角刷新重试。',
    loadFailedRetry: '账户信息加载失败，请点击刷新按钮重试。',
    permissionDeniedDb: '权限被拒绝。请检查数据库权限。',
    authError: '认证错误，请重新登录。',
    statConsumed: '已消耗',
    statRechargeTimes: '共 {n} 次',
    statGiftIncluded: '含赠送积分 {n}',
    statUsageRecords: '使用记录',
    compositionTitle: '积分构成',
    compositionRemaining: '剩余积分',
    compositionConsumedLabel: '已消耗积分',
    compositionGiftLabel: '赠送积分',
    ledgerSyncError: '账户记录同步异常，请刷新或联系客服',
  },
};

const UA_LANGS: UALang[] = ['en', 'zh'];

function getUALang(stored: string | null, prop?: Lang): UALang {
  if (stored === 'zh') return 'zh';
  if (stored === 'en') return 'en';
  return prop === 'zh' ? 'zh' : 'en';
}

interface UserAccountProps {
  userEmail: string;
  language?: Lang;
  isDarkMode: boolean;
  onClose: () => void;
  onOpenPaymentCenter: () => void;
}

type RechargeRecord = AccountRechargeRecord;

const UserAccount: React.FC<UserAccountProps> = ({
  userEmail,
  language = 'zh',
  isDarkMode,
  onClose,
  onOpenPaymentCenter,
}) => {
  const { accountInfo, remainingCredits, loading, error, syncStatus, ledgerSuspiciousEmpty, refreshAccount } = useAccount();
  const refreshAccountRef = useRef(refreshAccount);
  refreshAccountRef.current = refreshAccount;

  const rechargeHistory = useMemo(
    () => accountInfo?.history ?? [],
    [accountInfo?.history],
  );
  const totalRechargedCredits = useMemo(
    () => Math.floor(accountInfo?.total_points ?? 0),
    [accountInfo?.total_points],
  );

  // 与官网语种联动：优先使用 localStorage 中的 lang（与 index.html 一致），否则用 props
  const currentLang: UALang = typeof window !== 'undefined'
    ? getUALang(localStorage.getItem('lang'), language)
    : getUALang(null, language);
  const t = (key: string) => UA_STRINGS[currentLang]?.[key] ?? UA_STRINGS.en[key] ?? key;

  /** 单行：灰字 + 数字（字号为原 48px 的 70%，颜色与上方剩余积分数字一致 #D7CDA1）+ 灰字 */
  const renderQuotaLine = (templateKey: string, n: number) => {
    const template = t(templateKey);
    const parts = template.split('{n}');
    const muted = isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-600';
    const quotaNumClass =
      'text-[33.6px] font-bold leading-none tracking-wide inline-flex items-center shrink-0 text-[#D7CDA1]';
    if (parts.length !== 2) {
      return (
        <div className={`text-[15px] leading-snug ${muted}`}>
          {template.replace(/\{n\}/g, String(n))}
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-x-1 text-[15px] sm:text-[16px] leading-snug">
        <span className={`${muted} leading-snug`}>{parts[0]}</span>
        <span className={quotaNumClass}>{n}</span>
        <span className={`${muted} leading-snug`}>{parts[1]}</span>
      </div>
    );
  };

  // 头像与侧边栏联动：读取 Sidebar 保存的头像与裁切参数
  const avatarStorageKey = `nemo_avatar_${userEmail || 'guest'}`;
  const avatarTransformsKey = `nemo_avatar_transforms_${userEmail || 'guest'}`;
  const [uaAvatarId, setUaAvatarId] = useState<string>('01');
  const [uaAvatarTr, setUaAvatarTr] = useState<{ scale: number; x: number; y: number }>({ scale: 1, x: 0, y: 0 });
  const [uaAvatarMissing, setUaAvatarMissing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const load = () => {
      const id = localStorage.getItem(avatarStorageKey) || '01';
      setUaAvatarId(id);
      try {
        const raw = localStorage.getItem(avatarTransformsKey);
        if (raw) {
          const parsed = JSON.parse(raw) as Record<string, { scale: number; x: number; y: number }>;
          const tr = (parsed && parsed[id]) ? parsed[id] : { scale: 1, x: 0, y: 0 };
          setUaAvatarTr({
            scale: typeof tr.scale === 'number' ? tr.scale : 1,
            x: typeof tr.x === 'number' ? tr.x : 0,
            y: typeof tr.y === 'number' ? tr.y : 0,
          });
        } else {
          setUaAvatarTr({ scale: 1, x: 0, y: 0 });
        }
      } catch {
        setUaAvatarTr({ scale: 1, x: 0, y: 0 });
      }
    };
    load();
    const onStorage = (e: StorageEvent) => {
      if (e.key === avatarStorageKey || e.key === avatarTransformsKey) load();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [avatarStorageKey, avatarTransformsKey]);
  
  /** 打开账户中心时仅拉取一次（非 silent，显示一次同步） */
  useEffect(() => {
    void refreshAccountRef.current({ reason: 'user_account_open' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时刷新一次，禁止依赖 refreshAccount
  }, []);

  useEffect(() => {
    if (!userEmail) return;

    const emailNorm = userEmail.trim().toLowerCase();
    let pollingInterval: ReturnType<typeof setInterval> | null = null;
    let isPolling = false;

    const ch = supabase
      .channel(`user-balance-${emailNorm.replace(/[^a-zA-Z0-9_-]/g, '_')}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `email=eq.${emailNorm}`,
        },
        () => {
          void refreshAccountRef.current({ silent: true, reason: 'realtime_users_update' });
        },
      )
      .subscribe((status: string) => {
        if (status === 'CHANNEL_ERROR' && !pollingInterval && !isPolling) {
          isPolling = true;
          pollingInterval = setInterval(() => {
            void refreshAccountRef.current({ silent: true, reason: 'realtime_channel_error_poll' });
          }, 3000);
        }
      });

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      supabase.removeChannel(ch);
    };
  }, [userEmail]);

  const syncErrorMessage =
    error && (syncStatus === 'stale' || syncStatus === 'error')
      ? syncStatus === 'stale' && accountInfo
        ? `${error} (${t('loadFailedRetry')})`
        : error
      : null;

  const accountStatusLabel =
    syncStatus === 'syncing'
      ? t('accountSyncing')
      : syncStatus === 'stale'
        ? t('accountStale')
        : syncStatus === 'error' && !accountInfo
          ? t('accountError')
          : t('accountNormal');

  /** 唯一真相源：GET /api/credits/account 成功写入的 remaining_credits（按邮箱 lastKnownGood） */
  const pointsBalance = accountInfo != null ? Math.floor(accountInfo.remaining_credits) : null;
  const showPointsSyncing = syncStatus === 'syncing' && pointsBalance == null;
  const showPointsUnavailable =
    !loading && !showPointsSyncing && pointsBalance == null && (syncStatus === 'error' || syncStatus === 'stale');
  /** ledger 同步失败且无可用历史缓存时，统计区不展示「0 次 / 已消耗 0」 */
  const statsUnavailable = ledgerSuspiciousEmpty && rechargeHistory.length === 0;
  const totalEarnedCredits = loading || statsUnavailable ? 0 : totalRechargedCredits;
  const giftPointsForStat = loading || statsUnavailable ? 0 : Math.floor(accountInfo?.gift_points ?? 0);
  const consumedCredits =
    loading || pointsBalance == null || statsUnavailable
      ? 0
      : Math.max(0, totalEarnedCredits - pointsBalance);
  const rechargeTimesCount = statsUnavailable ? 0 : rechargeHistory.length;
  /** 按积分折算可执行次数（与后端扣费一致：渲染 20 分/次，放大 10 分/次） */
  const lightRenderSheets =
    loading || pointsBalance == null ? 0 : Math.floor(pointsBalance / CREDITS_PER_LIGHT_RENDER);
  const aiUpscaleSheets =
    loading || pointsBalance == null ? 0 : Math.floor(pointsBalance / CREDITS_PER_AI_UPSCALE);

  // 格式化日期
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(currentLang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** 充值历史标题：仅文案，无支付方式图标；普通入账统一为支付宝文案，后台手工为管理员文案 */
  const getRechargeHistoryTitle = (record: RechargeRecord) => {
    const pm = (record.payment_method || '').toLowerCase();
    if (pm === 'admin_manual') {
      return t('historyAdmin');
    }
    if (pm === 'admin_gift') {
      return t('historyAdminGift');
    }
    return t('historyAlipay');
  };

  return (
    <div
      className={`fixed inset-0 z-[3000] flex items-center justify-center backdrop-blur-md p-4 ${
        isDarkMode ? 'bg-black/75' : 'bg-zinc-900/35'
      }`}
      onClick={onClose}
      style={{ zIndex: 3000 }}
    >
      {/* 隐藏滚动条，仍可用滚轮/触控板滚动 */}
      <style>{`
        .nemo-user-account-modal {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .nemo-user-account-modal::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        .nemo-recharge-history-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${isDarkMode ? 'rgba(255,255,255,0.22) rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.22) rgba(0,0,0,0.06)'};
        }
        .nemo-recharge-history-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .nemo-recharge-history-scroll::-webkit-scrollbar-track {
          background: ${isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
          border-radius: 999px;
        }
        .nemo-recharge-history-scroll::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)'};
          border-radius: 999px;
        }
      `}</style>
      <div
        className="nemo-user-account-modal relative w-full max-w-[900px] min-h-[600px] max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-[10.8px] px-6 py-6 md:px-8 md:py-8 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          zIndex: 3001,
          background: isDarkMode ? '#242426' : '#FAFAFA',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: isDarkMode
            ? '0 28px 80px rgba(0, 0, 0, 0.52)'
            : '0 24px 64px rgba(0, 0, 0, 0.12)',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        }}
      >
        {/* 关闭按钮 */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void refreshAccount({ reason: 'user_account_manual_refresh' })}
            disabled={loading}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? 'text-white/35 hover:text-white/80 hover:bg-white/5'
                : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/80'
            }`}
            aria-label="Refresh"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              isDarkMode
                ? 'text-white/35 hover:text-white/80 hover:bg-white/5'
                : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/80'
            }`}
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 用户信息头部 */}
        <div className="flex justify-between items-center mb-6 pr-16">
          <div className="flex flex-col gap-1.5">
            <h1 className={`text-[26px] font-bold tracking-wide ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {t('userAccount')}
            </h1>
            <p className={`text-[18px] ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
              {loading ? '...' : (accountInfo?.email || userEmail)}
            </p>
          </div>
          <div
            className={`w-[60px] h-[60px] rounded-full flex items-center justify-center shrink-0 border overflow-hidden transition-colors ${
              isDarkMode ? 'bg-neutral-900/60 border-[#262626]' : 'bg-zinc-100 border-zinc-200'
            }`}
          >
            {!uaAvatarMissing && (
              <img
                src={`/avatars/${uaAvatarId}.png`}
                alt="avatar"
                draggable={false}
                className="w-full h-full select-none pointer-events-none"
                style={{
                  objectFit: 'contain',
                  // Sidebar 圆形头像基准直径为 42px，这里是 60px；平移按比例放大以保持裁切一致
                  transform: `translate(${uaAvatarTr.x * (60 / 42)}px, ${uaAvatarTr.y * (60 / 42)}px) scale(${uaAvatarTr.scale})`,
                  transformOrigin: 'center',
                }}
                onError={() => setUaAvatarMissing(true)}
                onLoad={() => setUaAvatarMissing(false)}
              />
            )}
            {uaAvatarMissing && (
              <svg className="w-7 h-7 text-[#C0C0C0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 8.25a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 20.25a8.25 8.25 0 0115 0" />
              </svg>
            )}
          </div>
        </div>

        {(syncStatus !== 'ok' && syncStatus !== 'idle') || syncErrorMessage ? (
          <div
            className={`mb-6 rounded-[7.2px] px-4 py-3 text-sm ${
              syncStatus === 'stale' || syncStatus === 'error'
                ? 'border border-amber-500/30 bg-amber-500/10 text-amber-200'
                : 'border border-[#D7CDA1]/20 bg-[#D7CDA1]/10 text-[#D7CDA1]'
            }`}
          >
            <div>{accountStatusLabel}</div>
            {syncErrorMessage && !loading ? <div className="mt-1 opacity-90">{syncErrorMessage}</div> : null}
          </div>
        ) : null}

        {/* 左右布局主要内容区 */}
        <div className="flex flex-col lg:flex-row gap-6 flex-1">
          {/* 左侧：账户概览 */}
          <div className="flex-1 flex flex-col gap-4">
            {/* 剩余积分大数字 */}
            <div className="mb-2">
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-[#10C35B]" />
                <span className={`text-[15px] ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                  {t('remainingPoints')}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  className={
                    loading
                      ? `text-[clamp(76px,10vw,120px)] font-black leading-none tracking-tighter ${isDarkMode ? 'text-white/20' : 'text-zinc-300'}`
                      : 'text-[clamp(76px,10vw,120px)] font-black leading-none tracking-tighter text-[#D7CDA1]'
                  }
                >
                  {loading || showPointsSyncing
                    ? '…'
                    : showPointsUnavailable || pointsBalance == null
                      ? '—'
                      : pointsBalance}
                </span>
                <span className={`text-lg font-medium ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>{t('pointsUnit')}</span>
              </div>
            </div>

            {/* 上方统计：三列（累计充值 / 累计获得 / 已消耗） */}
            <div
              className={`rounded-[12px] px-4 py-4 ${
                isDarkMode
                  ? 'bg-[#19191B] border border-white/5'
                  : 'bg-white border border-zinc-200 shadow-sm'
              }`}
            >
              <div className="grid grid-cols-3 gap-0">
                <div
                  className={`flex flex-col min-w-0 pr-3 ${
                    isDarkMode ? 'border-r border-white/[0.08]' : 'border-r border-zinc-200'
                  }`}
                >
                  <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>{t('totalRecharged')}</span>
                  <span className="mt-1 text-[22px] sm:text-[26px] font-bold text-[#D7CDA1] leading-tight tabular-nums">
                    {loading ? '¥--' : `¥${(accountInfo?.paid_amount || 0).toFixed(2)}`}
                  </span>
                  <span className={`mt-1.5 text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {statsUnavailable
                      ? '—'
                      : t('statRechargeTimes').replace(/\{n\}/g, String(rechargeTimesCount))}
                  </span>
                </div>
                <div
                  className={`flex flex-col min-w-0 px-3 ${
                    isDarkMode ? 'border-r border-white/[0.08]' : 'border-r border-zinc-200'
                  }`}
                >
                  <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>{t('totalPoints')}</span>
                  <span className="mt-1 flex flex-wrap items-baseline gap-x-1 text-[22px] sm:text-[26px] font-bold text-[#D7CDA1] leading-tight tabular-nums">
                    <span>{loading || statsUnavailable ? '--' : totalEarnedCredits}</span>
                    <span className="text-[15px] sm:text-[16px] font-semibold">{t('pointsUnit')}</span>
                  </span>
                  <span className={`mt-1.5 text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {statsUnavailable
                      ? '—'
                      : t('statGiftIncluded').replace(/\{n\}/g, String(giftPointsForStat))}
                  </span>
                </div>
                <div className="flex flex-col min-w-0 pl-3">
                  <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>{t('statConsumed')}</span>
                  <span className="mt-1 flex flex-wrap items-baseline gap-x-1 text-[22px] sm:text-[26px] font-bold text-[#D7CDA1] leading-tight tabular-nums">
                    <span>{loading || statsUnavailable ? '--' : consumedCredits}</span>
                    <span className="text-[15px] sm:text-[16px] font-semibold">{t('pointsUnit')}</span>
                  </span>
                  <span className={`mt-1.5 text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {t('statUsageRecords')} &gt;
                  </span>
                </div>
              </div>
            </div>

            {/* 积分构成：三列（剩余 / 已消耗 / 赠送） */}
            <div
              className={`rounded-[12px] px-4 py-4 ${
                isDarkMode
                  ? 'bg-[#19191B] border border-white/5'
                  : 'bg-white border border-zinc-200 shadow-sm'
              }`}
            >
              <div className={`mb-3 text-[15px] font-medium ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {t('compositionTitle')}
              </div>
              <div className="grid grid-cols-3 gap-0">
                <div
                  className={`flex flex-col items-center text-center min-w-0 px-2 ${
                    isDarkMode ? 'border-r border-white/[0.08]' : 'border-r border-zinc-200'
                  }`}
                >
                  <span className="text-sm font-medium text-[#D7CDA1]">{t('compositionRemaining')}</span>
                  <div className="mt-1.5 flex items-baseline justify-center gap-x-1 flex-wrap">
                    <span className="text-[22px] sm:text-[24px] font-bold text-[#D7CDA1] tabular-nums">
                      {loading || showPointsSyncing || pointsBalance == null ? '—' : pointsBalance}
                    </span>
                    <span className="text-[15px] font-semibold text-[#D7CDA1]">{t('pointsUnit')}</span>
                  </div>
                  <span className="mt-2 inline-block h-1 w-10 shrink-0 rounded-full bg-[#D7CDA1]" aria-hidden />
                </div>
                <div
                  className={`flex flex-col items-center text-center min-w-0 px-2 ${
                    isDarkMode ? 'border-r border-white/[0.08]' : 'border-r border-zinc-200'
                  }`}
                >
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {t('compositionConsumedLabel')}
                  </span>
                  <div className="mt-1.5 flex items-baseline justify-center gap-x-1 flex-wrap">
                    <span className="text-[22px] sm:text-[24px] font-bold text-[#D7CDA1] tabular-nums">
                      {loading ? '—' : consumedCredits}
                    </span>
                    <span className="text-[15px] font-semibold text-[#D7CDA1]">{t('pointsUnit')}</span>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center min-w-0 px-2">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {t('compositionGiftLabel')}
                  </span>
                  <div className="mt-1.5 flex items-baseline justify-center gap-x-1 flex-wrap">
                    <span className="text-[22px] sm:text-[24px] font-bold text-[#D7CDA1] tabular-nums">
                      {loading ? '—' : giftPointsForStat}
                    </span>
                    <span className="text-[15px] font-semibold text-[#D7CDA1]">{t('pointsUnit')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：充值历史（列表区独立固定高度 + 细滚动条） */}
          <div className="flex-1 flex flex-col min-h-0">
            <div
              className={`rounded-[12px] px-4 py-4 flex flex-col flex-1 min-h-0 ${
                isDarkMode
                  ? 'bg-[#19191B] border border-white/5'
                  : 'bg-white border border-zinc-200 shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between mb-4 shrink-0">
                <span className={`text-[16px] font-medium ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {t('rechargeHistory')}
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                  {rechargeHistory.length > 0
                    ? currentLang === 'zh'
                      ? `共 ${rechargeHistory.length} 条记录`
                      : `${rechargeHistory.length} records`
                    : ''}
                </span>
              </div>

              <div
                className={`nemo-recharge-history-scroll min-h-[220px] h-[min(42vh,320px)] max-h-[320px] overflow-y-auto overflow-x-hidden pr-1 shrink-0 ${
                  loading || rechargeHistory.length === 0 ? 'flex items-center justify-center' : ''
                }`}
              >
                {loading ? (
                  <div className={`text-center py-6 ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {currentLang === 'zh' ? '加载中...' : 'Loading...'}
                  </div>
                ) : ledgerSuspiciousEmpty && rechargeHistory.length === 0 ? (
                  <div className={`text-center py-6 px-3 ${isDarkMode ? 'text-amber-200/90' : 'text-amber-700'}`}>
                    {t('ledgerSyncError')}
                  </div>
                ) : rechargeHistory.length === 0 ? (
                  <div className={`text-center py-6 ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                    {t('noRechargeHistory')}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rechargeHistory.map((record) => (
                      <div
                        key={record.id}
                        className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a1a1c]' : 'bg-zinc-50'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            {getRechargeHistoryTitle(record)}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                            {formatDate(record.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4">
                            <span className={isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-600'}>
                              {t('rechargeAmount')}:{' '}
                              <span className="font-medium text-[#D7CDA1]">¥{record.purchase_price}</span>
                            </span>
                            <span className={isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-600'}>
                              {t('rechargeCredits')}: <span className="font-medium">{record.amount_total}</span>
                            </span>
                          </div>
                          <span className={`text-xs ${isDarkMode ? 'text-[#8A8A8E]' : 'text-zinc-500'}`}>
                            {t('expireDate')}: {formatDate(record.expires_at).split(' ')[0]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 立即充值按钮 */}
        <button
          onClick={() => {
            onClose();
            onOpenPaymentCenter();
          }}
          className="w-full bg-[#D7CDA1] text-[#1A1A1C] font-bold text-[18px] py-[1.3rem] rounded-[7.2px] mt-6 transition-opacity hover:opacity-90"
        >
          {t('rechargeNow')}
        </button>
      </div>
    </div>
  );
};

export default UserAccount;